import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Ellipsis,
  File,
  Mail,
  MessageCircleCode,
  ShieldMinus,
  Sigma,
  SquarePen,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useQuery } from "react-query";
import { getLoanProductById } from "../../sdk/products/products";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { hasPermission } from "../../utils/permissions";
import useAuth from "../../hooks/useAuth";
import { SkeletonLoansPage } from "../../skeletons/LoansSkeletons";

const LoanProduct = () => {
  const navigate = useNavigate();
  const formatAmount = useFormatAmount();
  const formatDate = useFormattedDate();
  const { id } = useParams();
  const { auth } = useAuth();

  const { data: loanProduct, isFetching } = useQuery({
    queryKey: [id],
    queryFn: () => getLoanProductById(id),
    initialData: {},
  });

  return (
    <>
      {isFetching ? (
        <SkeletonLoansPage />
      ) : (
        <div>
          <div className="flex items-center gap-[5px] my-[10px]">
            <ChevronLeft color="#000000" size={17} />
            <p
              onClick={() => navigate("/dashboard/loan-products")}
              className="text-[#222222] text-[16px] cursor-pointer font-bold"
            >
              Products
            </p>
            <ChevronRight color="#000000" size={17} />
            <p className="text-[#222222] text-[16px] font-bold">
              {loanProduct?.name}
            </p>
          </div>
          <div className="h-[145px] bg-[#222222] border-l-[1px] w-full py-[20px] pl-[10px] pr-[40px] flex justify-between">
            <div className="flex gap-[20px]">
              <div className="h-[94px] w-[94px] uppercase font-bold bg-[#FFFFFF33] flex items-center justify-center rounded-[20px] text-[40px] text-[#F3F3F3]">
                {loanProduct?.name?.length > 0 ? loanProduct?.name[0] : "D"}
              </div>
              <div className="h-full flex justify-around flex-col">
                <p className="text-white font-light text-[14px]">
                  #{loanProduct?.public_id}
                </p>
                <div className="flex items-center gap-[10px]">
                  <p className="font-medium text-[16px] text-white">
                    {loanProduct?.name}
                  </p>
                  <div className="flex items-center gap-[5px]">
                    <Circle size={17} color="#00A676" fill="#00A676" />
                    <p className="text-[#00A676] text-[14px] font-normal">
                      Active
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                    Created on
                  </p>
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                    Created by
                  </p>
                </div>
                <div className="flex items-start">
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                    {formatDate(loanProduct?.createdAt)}
                  </p>
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                    {loanProduct?.user
                      ? `${loanProduct.user.firstname} ${loanProduct.user.lastname}`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-[20px]">
              <div className="w-[44px] h-[40px] border border-[#F3F3F3] rounded-[5px] flex items-center justify-center">
                <Ellipsis color="white" />
              </div>
              <button className="h-[40px] bg-[#FFFFFF] px-[20px] rounded-[30px] text-[15px] text-[#000000] font-medium flex items-center justify-center gap-[10px]">
                <Mail size={22} color="#000000" />
                Send Email
              </button>
            </div>
          </div>
          <div className="flex my-[20px] pr-[40px] pl-[10px] justify-between">
            <div className="w-[49%]">
              <div className="h-[785px] p-[20px] w-full bg-[#ffffff] rounded-[10px] border border-[#A8A8A8]">
                <div className="flex items-center justify-between">
                  <p className="text-[#000000] font-semibold text-[20px]">
                    Product Overview
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Loan Name
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.name}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Loan Type
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.loanType ?? "N/A"}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Interest Rate
                  </p>
                  <p className="text-[#000000] capitalize font-bold text-[16px]">
                    {loanProduct?.defaultInterestRate}% (
                    {loanProduct?.repaymentType})
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Repayment Frequency
                  </p>
                  <p className="text-[#000000] capitalize font-bold text-[16px]">
                    {loanProduct?.repaymentFrequency}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Minimum duration
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.minLoanPeriod ?? 1}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Maximum duration
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.maxLoanPeriod ?? 1}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Loan Amount
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {formatAmount(loanProduct?.minLoanAmount)} –{" "}
                    {formatAmount(loanProduct?.maxLoanAmount)}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Collateral Required
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">No</p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Processing Fee
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.processingFee ?? 0}%
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Penalty Rate
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {loanProduct?.penaltyFee ?? 0}% per late installment
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Status
                  </p>
                  <p className="text-[#000000] capitalize font-bold text-[16px]">
                    {loanProduct?.status}
                  </p>
                </div>
              </div>
              <div className="w-full h-[142px] overflow-y-auto my-[20px] p-[10px] rounded-[10px] bg-white border border-[#A8A8A8]">
                <p className="text-[#000000] text-[20px] font-semibold">
                  Description / Purpose
                </p>
                <p className="text-[#5C5C5C] font-normal text-[14px]">
                  {loanProduct?.description ?? "N/A"}
                </p>
              </div>
            </div>
            <div className="w-[49%]">
              <div className="h-[227px] mb-[20px] px-[15px] py-[15px] w-full border border-[#00A676] rounded-[10px] bg-white">
                <p className="text-[#000000] text-[20px] font-semibold">
                  Quick Analytics
                </p>
                <div className="flex items-center justify-between">
                  <div className="h-[150px] w-[47%] flex bg-[#FBF5F5] rounded-[15px]">
                    <div
                      style={{ backgroundColor: "#4B5D67" }}
                      className="w-[5px] h-[124px] my-auto mr-[10px]"
                    ></div>
                    <div className="h-full w-full p-[10px] flex flex-col justify-between">
                      <div className="flex items-start gap-[5px]">
                        <div className="bg-[#4B5D6712] rounded-[100%] h-[31px] w-[31px] flex items-center justify-center">
                          <Sigma />
                        </div>
                        <p className="text-[14px] mt-[3px] font-normal">
                          Total Disbursed
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <p className="font-bold text-[26px] text-[#222222]">
                          {formatAmount(
                            loanProduct?.stats?.totalAmountDisbursed ?? 0,
                          )}
                        </p>
                      </div>
                      <div></div>
                    </div>
                  </div>
                  <div className="h-[150px] w-[47%] flex bg-[#FBF5F5] rounded-[15px]">
                    <div
                      style={{ backgroundColor: "#00A676" }}
                      className="w-[5px] h-[124px] my-auto mr-[10px]"
                    ></div>
                    <div className="h-full w-full p-[10px] flex flex-col justify-between">
                      <div className="flex items-start gap-[5px]">
                        <div className="bg-[#4B5D6712] rounded-[100%] h-[31px] w-[31px] flex items-center justify-center">
                          <Sigma color="#00A676" />
                        </div>
                        <p className="text-[14px] text-[#00A676] mt-[3px] font-normal">
                          No. of Borrowers
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <p className="font-bold text-[26px] text-[#222222]">
                          {loanProduct?.stats?.numberOfBorrowers ?? 0}
                        </p>
                      </div>
                      <div></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-[465px] p-[20px] w-full bg-[#ffffff] rounded-[10px] border border-[#A8A8A8]">
                <p className="text-[#000000] text-[20px] font-semibold">
                  Quick Actions
                </p>
                {hasPermission(
                  auth?.roles,
                  "f5ef818a-fcd9-4c1c-072a-02b87bdc87f2",
                ) && (
                  <div
                    onClick={() =>
                      navigate(`/dashboard/loan-products/${id}/edit-product`)
                    }
                    className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]"
                  >
                    <div className="w-[10%] flex items-center justify-center h-full">
                      <SquarePen size={18} color="#000000" />
                    </div>
                    <div className="w-[85%] flex flex-col justify-center h-full">
                      <p className="text-[#000000] font-medium text-[14px]">
                        Edit Product
                      </p>
                      <p className="text-[#737373] text-[12px] font-normal">
                        Update the loan product’s details
                      </p>
                    </div>
                    <div className="w-[5%] flex items-center justify-center h-full">
                      <ChevronRight color="#000000" size={24} />
                    </div>
                  </div>
                )}
                <div className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <MessageCircleCode size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      View Applications & Active Borrowers
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      See all submitted loan applications for this product
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                <div className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <File size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      Reports
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      Access performance data for this loan product
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                <div className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <ShieldMinus size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      Deactivate Product
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      Temporarily disable this loan product to prevent new
                      applications
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
              </div>
              <div className="w-full h-[213px] overflow-y-auto my-[20px] p-[10px] rounded-[10px] bg-white border border-[#A8A8A8]">
                <p className="text-[#000000] text-[20px] font-semibold">
                  Terms & Conditions
                </p>
                <p className="text-[#000000] underline mb-[10px] text-[16px] font-normal">
                  Terms & Conditions
                </p>
                <p className="text-[#5C5C5C] font-normal text-[16px]">
                  Grace Period: {loanProduct?.gracePeriod ?? 0} Month <br />
                  Eligibility: Minimum age 18
                  <br />
                  Insurance Fee: {loanProduct?.insuranceFee ?? 0}% <br />
                  Early Repayment Allowed: Yes
                </p>
                <p className="text-[#222222] text-[15px] font-normal">
                  {loanProduct?.loanTermsAndConditions}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanProduct;
